import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { cx } from './cx.ts';
import { IconButton } from './Button.tsx';

export function Page({ children, className, wide }: { children: ReactNode; className?: string; wide?: boolean }) {
  return (
    <div className={cx('mx-auto w-full px-4 pb-[calc(112px+env(safe-area-inset-bottom))] lg:pb-16', wide ? 'max-w-[960px]' : 'max-w-[640px]', className)}>
      {children}
    </div>
  );
}

/** Sticky top bar with an optional back button. Title stays left-aligned and calm. */
export function TopBar({ title, subtitle, back, actions, large }: {
  title?: ReactNode;
  subtitle?: ReactNode;
  back?: string | true;
  actions?: ReactNode;
  large?: boolean;
}) {
  const navigate = useNavigate();
  return (
    <header className="safe-top sticky top-0 z-30 bg-bg/88 backdrop-blur-xl supports-[backdrop-filter]:bg-bg/75">
      <div className="mx-auto flex h-14 w-full max-w-[640px] items-center gap-1 px-2 lg:max-w-[960px]">
        {back && (
          <IconButton
            label="Back"
            onClick={() => (typeof back === 'string' ? navigate(back) : window.history.length > 1 ? navigate(-1) : navigate('/'))}
          >
            <ArrowLeft className="size-[22px]" />
          </IconButton>
        )}
        {!large && (
          <div className={cx('min-w-0 flex-1', !back && 'pl-2')}>
            {title && <h1 className="truncate text-[17px] font-bold tracking-[-0.01em] text-ink">{title}</h1>}
            {subtitle && <p className="-mt-0.5 truncate text-[12.5px] text-ink-3">{subtitle}</p>}
          </div>
        )}
        {large && <div className="flex-1" />}
        {actions && <div className="flex items-center gap-0.5">{actions}</div>}
      </div>
      {large && title && (
        <div className="mx-auto w-full max-w-[640px] px-4 pb-2 lg:max-w-[960px]">
          <h1 className="text-[28px] font-bold tracking-[-0.025em] text-ink">{title}</h1>
          {subtitle && <p className="text-[14px] text-ink-2">{subtitle}</p>}
        </div>
      )}
    </header>
  );
}

export function Card({ children, className, as: As = 'div', padded = true }: {
  children: ReactNode; className?: string; as?: 'div' | 'section' | 'article'; padded?: boolean;
}) {
  return <As className={cx('rounded-[20px] border border-line bg-surface shadow-card', padded && 'p-4', className)}>{children}</As>;
}

export function Section({ title, action, children, className }: { title?: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx('mt-6', className)}>
      {(title || action) && (
        <div className="mb-2 flex items-end justify-between px-1">
          {title && <h2 className="text-[13px] font-bold uppercase tracking-[0.06em] text-ink-3">{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

/** A tappable row inside a list card. */
export function Row({ to, onClick, leading, title, subtitle, trailing, chevron, className, danger }: {
  to?: string;
  onClick?: () => void;
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  chevron?: boolean;
  className?: string;
  danger?: boolean;
}) {
  const inner = (
    <>
      {leading}
      <span className="min-w-0 flex-1">
        <span className={cx('block truncate text-[15.5px] font-semibold', danger ? 'text-out' : 'text-ink')}>{title}</span>
        {subtitle && <span className="mt-0.5 block truncate text-[13px] text-ink-3">{subtitle}</span>}
      </span>
      {trailing}
      {chevron && <ChevronRight className="size-[18px] shrink-0 text-ink-3" />}
    </>
  );
  const cls = cx('flex w-full items-center gap-3 px-4 py-3 text-left', (to || onClick) && 'press hover:bg-surface-2/60', className);
  if (to) return <Link to={to} className={cls}>{inner}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className={cls}>{inner}</button>;
  return <div className={cls}>{inner}</div>;
}

export function List({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('overflow-hidden rounded-[20px] border border-line bg-surface shadow-card [&>*+*]:border-t [&>*+*]:border-line', className)}>
      {children}
    </div>
  );
}

export function EmptyState({ icon, title, body, action, className }: {
  icon?: ReactNode; title: ReactNode; body?: ReactNode; action?: ReactNode; className?: string;
}) {
  return (
    <div className={cx('flex flex-col items-center px-6 py-12 text-center', className)}>
      {icon && <div className="mb-4 flex size-16 items-center justify-center rounded-[22px] bg-surface-2 text-ink-2">{icon}</div>}
      <h3 className="text-[17px] font-bold text-ink">{title}</h3>
      {body && <p className="mt-1.5 max-w-[300px] text-[14.5px] leading-relaxed text-ink-2">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function SkeletonList({ rows = 5 }: { rows?: number }) {
  return (
    <div className="overflow-hidden rounded-[20px] border border-line bg-surface">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 border-t border-line px-4 py-3.5 first:border-t-0">
          <div className="skeleton size-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="skeleton h-3.5 w-2/5 rounded" />
            <div className="skeleton h-3 w-1/4 rounded" />
          </div>
          <div className="skeleton h-4 w-16 rounded" />
        </div>
      ))}
    </div>
  );
}

/** Primary actions pinned above the keyboard/home bar on form screens. */
export function ActionBar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/92 backdrop-blur-xl lg:left-[248px]', className)}>
      <div className="safe-bottom mx-auto flex w-full max-w-[640px] gap-2.5 px-4 pb-3 pt-3">{children}</div>
    </div>
  );
}

export function Badge({ children, tone = 'neutral', className }: {
  children: ReactNode; tone?: 'neutral' | 'in' | 'out' | 'warn' | 'brand' | 'accent'; className?: string;
}) {
  const t = {
    neutral: 'bg-surface-2 text-ink-2',
    in: 'bg-in-soft text-in',
    out: 'bg-out-soft text-out',
    warn: 'bg-warn-soft text-warn',
    brand: 'bg-brand-soft text-brand',
    accent: 'bg-accent-soft text-accent',
  }[tone];
  return <span className={cx('inline-flex h-6 items-center rounded-full px-2.5 text-[12px] font-bold tracking-[0.01em]', t, className)}>{children}</span>;
}

const AVATAR_TONES = [
  'bg-[#e8e2f4] text-[#4b3a7a] dark:bg-[#2c2640] dark:text-[#c8b9f0]',
  'bg-[#e0ecf4] text-[#2a5574] dark:bg-[#1d2c38] dark:text-[#9cc6e6]',
  'bg-[#f4e6d8] text-[#7a4a1e] dark:bg-[#3a2b1d] dark:text-[#ecc196]',
  'bg-[#e2efe4] text-[#2f6440] dark:bg-[#1c3123] dark:text-[#9fd8ae]',
  'bg-[#f3e1e3] text-[#7d3442] dark:bg-[#3a1f25] dark:text-[#f0aab7]',
  'bg-[#ece8dc] text-[#5c5440] dark:bg-[#2f2c23] dark:text-[#d6cba8]',
];

export function Avatar({ name, size = 40, className }: { name: string; size?: number; className?: string }) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const initials = ((words[0]?.[0] ?? '?') + (words.length > 1 ? words[words.length - 1]![0] : words[0]?.[1] ?? '')).toUpperCase();
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return (
    <span
      aria-hidden
      className={cx('inline-flex shrink-0 items-center justify-center rounded-full font-bold', AVATAR_TONES[h % AVATAR_TONES.length], className)}
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initials}
    </span>
  );
}

export function IconTile({ children, tone = 'neutral', size = 40 }: { children: ReactNode; tone?: 'neutral' | 'in' | 'out' | 'brand' | 'warn' | 'accent'; size?: number }) {
  const t = {
    neutral: 'bg-surface-2 text-ink-2',
    in: 'bg-in-soft text-in',
    out: 'bg-out-soft text-out',
    brand: 'bg-brand-soft text-brand',
    warn: 'bg-warn-soft text-warn',
    accent: 'bg-accent-soft text-accent',
  }[tone];
  return <span className={cx('inline-flex shrink-0 items-center justify-center rounded-[14px]', t)} style={{ width: size, height: size }}>{children}</span>;
}

export function KeyValue({ label, value, strong }: { label: ReactNode; value: ReactNode; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className={cx('text-[14.5px]', strong ? 'font-semibold text-ink' : 'text-ink-2')}>{label}</span>
      <span className={cx('text-right text-[14.5px] tnum', strong ? 'font-bold text-ink' : 'text-ink')}>{value}</span>
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: Error | null; onRetry?: () => void }) {
  if (!error) return null;
  return (
    <div role="alert" className="rounded-2xl border border-out/25 bg-out-soft px-4 py-3 text-[14.5px] text-out">
      <p className="font-semibold">{error.message}</p>
      {onRetry && <button type="button" onClick={onRetry} className="mt-1 font-bold underline underline-offset-2">Try again</button>}
    </div>
  );
}
